import React from 'react';
import { useApi } from '@/api/ApiContext';
import { getImageDisplayUrl } from '@/api/handlers/resources/images';
import type { BrandListQuery } from '@/api/handlers/resources/brands/requests';
import type { ErrorResponse } from '@/api/handlers/responses';
import type { BrandListItem } from '@/api/models';
import {
    useBrandListQuery,
    useDeleteBrandMutation,
    useUpdateBrandMutation,
} from '@/api/queries';
import { useDeleteConfirm } from '@/components/deleteConfirmDialog';
import { useCreateBrand } from './useCreateBrand';
import { BRANDS_PAGE_SIZE, type Brand } from './components/brandsMeta';
import type {
    BrandFormInitialValues,
    BrandFormSubmit,
} from './components/brandFormModal/useBrandForm';

/** Debounce window for the search box before a new request fires. */
const SEARCH_DEBOUNCE_MS = 300;

export type UseBrandsPageResult = {
    /** Drives the delete confirmation and surfaces a refusal inline. */
    deleteConfirm: ReturnType<typeof useDeleteConfirm>;
    data: Brand[] | null;
    editingInitialValues: BrandFormInitialValues | null;
    error: ErrorResponse | null;
    formError: string | null;
    formMode: 'create' | 'edit';
    isFormOpen: boolean;
    isLoading: boolean;
    isSubmitting: boolean;
    onAddBrand: () => void;
    onCloseForm: () => void;
    onDeleteBrand: (id: string) => void;
    onEditBrand: (id: string) => void;
    onPageChange: (page: number) => void;
    onPageSizeChange: (pageSize: number) => void;
    onRetry: () => void;
    onSearchChange: (value: string) => void;
    onSubmitBrand: (input: BrandFormSubmit) => void;
    page: number;
    pageSize: number;
    search: string;
    totalCount: number;
    totalPages: number;
};

/**
 * Drives the Brands page. The list is **server-paginated** (`/brands/list`
 * takes page / pageSize / search and returns the matching page plus totalCount
 * / totalPages, BOS-81), so search and paging are pushed to the API rather than
 * done client-side — the whole brand set is never loaded at once.
 *
 * Create is one multipart POST; edit goes through `POST /images?purpose=brands`
 * first when the user picked a fresh image, then PUT with the resolved URL. All
 * writes invalidate the cached pages.
 */
export const useBrandsPage = (): UseBrandsPageResult => {
    const { apiClient, baseUrl } = useApi();

    const [page, setPage] = React.useState(1);
    const [pageSize, setPageSize] = React.useState(BRANDS_PAGE_SIZE);
    const [search, setSearch] = React.useState('');
    const [debouncedSearch, setDebouncedSearch] = React.useState('');

    const [isFormOpen, setIsFormOpen] = React.useState(false);
    const [editingId, setEditingId] = React.useState<string | null>(null);
    const [formError, setFormError] = React.useState<string | null>(null);
    const [isEditUploading, setIsEditUploading] = React.useState(false);

    // Debounce the term that actually drives the request so we don't fire a
    // query per keystroke.
    React.useEffect(() => {
        const handle = setTimeout(
            () => setDebouncedSearch(search),
            SEARCH_DEBOUNCE_MS,
        );
        return () => clearTimeout(handle);
    }, [search]);

    // A new search starts from page 1.
    React.useEffect(() => {
        setPage(1);
    }, [debouncedSearch]);

    const query: BrandListQuery = {
        page,
        pageSize,
        search: debouncedSearch.trim() || undefined,
    };
    const brandsQuery = useBrandListQuery(query);

    const response =
        brandsQuery.data && brandsQuery.data.success
            ? brandsQuery.data.response
            : null;

    // Raw items as the server returned them — kept verbatim so the edit handler
    // can seed the form with the *original* relative imageUrl.
    const rawItems: BrandListItem[] | null = response?.items ?? null;

    const data: Brand[] | null = React.useMemo(() => {
        if (rawItems === null) {
            return null;
        }
        return rawItems.map((item) => ({
            ...item,
            imageUrl: item.imageUrl
                ? (getImageDisplayUrl(item.imageUrl, baseUrl) ?? null)
                : null,
        }));
    }, [baseUrl, rawItems]);

    const error: ErrorResponse | null =
        brandsQuery.data && !brandsQuery.data.success
            ? brandsQuery.data.error
            : null;

    const editingRaw = React.useMemo(() => {
        if (editingId === null || rawItems === null) {
            return null;
        }
        return rawItems.find((item) => item.id === editingId) ?? null;
    }, [editingId, rawItems]);

    const editingInitialValues: BrandFormInitialValues | null =
        React.useMemo(() => {
            if (editingRaw === null) {
                return null;
            }
            return {
                name: editingRaw.name,
                image: editingRaw.imageUrl
                    ? {
                          imageName: editingRaw.name,
                          imageUrl:
                              getImageDisplayUrl(
                                  editingRaw.imageUrl,
                                  baseUrl,
                              ) ?? editingRaw.imageUrl,
                      }
                    : null,
            };
        }, [baseUrl, editingRaw]);

    const { create, isCreating } = useCreateBrand();
    const updateMutation = useUpdateBrandMutation();
    const deleteMutation = useDeleteBrandMutation();

    const onSearchChange = React.useCallback((value: string) => {
        setSearch(value);
    }, []);

    const onPageChange = React.useCallback((next: number) => {
        setPage(next);
    }, []);

    const onPageSizeChange = React.useCallback((next: number) => {
        setPageSize(next);
        setPage(1);
    }, []);

    const onAddBrand = React.useCallback(() => {
        setEditingId(null);
        setFormError(null);
        setIsFormOpen(true);
    }, []);

    const onEditBrand = React.useCallback((id: string) => {
        setEditingId(id);
        setFormError(null);
        setIsFormOpen(true);
    }, []);

    const onCloseForm = React.useCallback(() => {
        setIsFormOpen(false);
        setEditingId(null);
        setFormError(null);
    }, []);

    // The dialog owns the refusal: a delete can now come back with a
    // sentence naming what depends on the record (bos-api BOS-121).
    const deleteConfirm = useDeleteConfirm({
        onDelete: (id) => deleteMutation.mutateAsync(id),
    });

    const onDeleteBrand = React.useCallback(
        (id: string) => {
            const item = data?.find((row) => row.id === id);
            deleteConfirm.onRequestDelete({ id, name: item?.name ?? id });
        },
        [data, deleteConfirm],
    );

    const onRetry = React.useCallback(() => {
        brandsQuery.refetch();
    }, [brandsQuery]);

    const onSubmitBrand = React.useCallback(
        async (input: BrandFormSubmit) => {
            setFormError(null);

            if (editingId === null) {
                // ---- Create flow (single multipart round-trip) ------------
                // Shared with the Control Center's "Add brand", which opens
                // this same form from the launcher — see `useCreateBrand`.
                const created = await create(input);
                if (!created.ok) {
                    setFormError(created.message);
                    return;
                }
                setIsFormOpen(false);
                return;
            }

            // ---- Edit flow ----------------------------------------------
            let nextImageUrl: string | null = input.imageUrl;
            if (input.imageUpload) {
                setIsEditUploading(true);
                try {
                    const uploadResult = await apiClient.images.uploadImage(
                        input.imageUpload,
                        'brands',
                    );
                    if (!uploadResult.success) {
                        setFormError(
                            uploadResult.error.message ??
                                'Failed to upload image.',
                        );
                        return;
                    }
                    nextImageUrl = uploadResult.response.imageUrl;
                } finally {
                    setIsEditUploading(false);
                }
            }

            const updateResult = await updateMutation.mutateAsync({
                id: editingId,
                body: {
                    name: input.name,
                    imageUrl: nextImageUrl,
                },
            });
            if (!updateResult.success) {
                setFormError(
                    updateResult.error.message ?? 'Failed to update brand.',
                );
                return;
            }
            setEditingId(null);
            setIsFormOpen(false);
        },
        [apiClient, create, editingId, updateMutation],
    );

    return {
        deleteConfirm,
        data,
        editingInitialValues,
        error,
        formError,
        formMode: editingId === null ? 'create' : 'edit',
        isFormOpen,
        isLoading: brandsQuery.isLoading,
        isSubmitting: isCreating || updateMutation.isPending || isEditUploading,
        onAddBrand,
        onCloseForm,
        onDeleteBrand,
        onEditBrand,
        onPageChange,
        onPageSizeChange,
        onRetry,
        onSearchChange,
        onSubmitBrand,
        page,
        pageSize,
        search,
        totalCount: response?.totalCount ?? 0,
        totalPages: response?.totalPages ?? 1,
    };
};
